import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Flame,
  Send,
  MessageCircle,
  Users as UsersIcon,
  Lock,
  Check,
  X,
  Clock,
  Hash,
  LogOut,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const COMMON_ROOM = "mahfil";
const POLL_MS = 3000;

function timeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function otherUserIdFromRoom(room, meId) {
  if (!room || !room.startsWith("pm:")) return null;
  const parts = room.split(":");
  if (parts.length !== 3) return null;
  return parts[1] === meId ? parts[2] : parts[1];
}

export default function Mahfil() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const meId = user?.id;

  const [activeRoom, setActiveRoom] = useState(COMMON_ROOM);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const seenRequestsRef = useRef(new Set());
  const scrollRef = useRef(null);

  const fetchMessages = useCallback(async (room) => {
    try {
      const { data } = await api.get(`/mahfil/messages?room=${encodeURIComponent(room)}`);
      setMessages(data);
    } catch (err) {
      // 401 is handled by ProtectedRoute; other transient failures are ignored on poll.
      if (err?.response?.status && err.response.status >= 500) {
        toast.error(formatApiError(err));
      }
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/mahfil/users");
      setUsers(data);
    } catch (err) {
      if (err?.response?.status && err.response.status >= 500) {
        toast.error(formatApiError(err));
      }
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/mahfil/requests");
      setRequests(data);
      // Toast for new incoming pending requests
      data
        .filter((r) => r.to_user_id === meId && r.status === "pending" && !seenRequestsRef.current.has(r.id))
        .forEach((r) => {
          seenRequestsRef.current.add(r.id);
          toast(`${r.from_pseudonym} wants to talk in private`, {
            description: "Tap the request panel to accept or decline.",
          });
        });
    } catch (err) {
      if (err?.response?.status && err.response.status >= 500) {
        toast.error(formatApiError(err));
      }
    }
  }, [meId]);

  // Initial + poll
  useEffect(() => {
    if (!meId) return;
    fetchMessages(activeRoom);
    fetchUsers();
    fetchRequests();
    const id = setInterval(() => {
      fetchMessages(activeRoom);
      fetchUsers();
      fetchRequests();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [activeRoom, meId, fetchMessages, fetchUsers, fetchRequests]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, activeRoom]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/mahfil/messages", { room: activeRoom, body: text });
      setMessages((m) => [...m, data]);
      setBody("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const askPrivate = async (u) => {
    try {
      const { data } = await api.post("/mahfil/requests", { to_user_id: u.id });
      setRequests((r) => {
        const i = r.findIndex((x) => x.id === data.id);
        if (i >= 0) {
          const copy = [...r];
          copy[i] = data;
          return copy;
        }
        return [data, ...r];
      });
      if (data.status === "accepted") {
        toast.success(`Private room with ${u.pseudonym} is open.`);
        setActiveRoom(data.room);
      } else {
        toast.success(`Request sent to ${u.pseudonym}. Waiting for them to accept.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const accept = async (r) => {
    try {
      const { data } = await api.post(`/mahfil/requests/${r.id}/accept`);
      setRequests((prev) => prev.map((x) => (x.id === r.id ? data : x)));
      toast.success(`You are now in a private room with ${r.from_pseudonym}.`);
      setActiveRoom(data.room);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const decline = async (r) => {
    try {
      const { data } = await api.post(`/mahfil/requests/${r.id}/decline`);
      setRequests((prev) => prev.map((x) => (x.id === r.id ? data : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  // Derived: my accepted private rooms
  const acceptedPrivateRooms = useMemo(() => {
    if (!meId) return [];
    const seen = new Set();
    return requests
      .filter((r) => r.status === "accepted" && (r.from_user_id === meId || r.to_user_id === meId))
      .filter((r) => {
        if (seen.has(r.room)) return false;
        seen.add(r.room);
        return true;
      })
      .map((r) => {
        const otherPseudonym = r.from_user_id === meId ? r.to_pseudonym : r.from_pseudonym;
        const otherId = r.from_user_id === meId ? r.to_user_id : r.from_user_id;
        return { room: r.room, otherPseudonym, otherId };
      });
  }, [requests, meId]);

  const pendingIncoming = useMemo(
    () => requests.filter((r) => r.to_user_id === meId && r.status === "pending"),
    [requests, meId],
  );
  const pendingOutgoing = useMemo(
    () => requests.filter((r) => r.from_user_id === meId && r.status === "pending"),
    [requests, meId],
  );

  const activeHeader = useMemo(() => {
    if (activeRoom === COMMON_ROOM) {
      return { title: "The Mahfil", subtitle: "A common room. Everyone hears everyone.", icon: Hash };
    }
    const otherId = otherUserIdFromRoom(activeRoom, meId);
    const match = acceptedPrivateRooms.find((r) => r.room === activeRoom);
    return {
      title: match?.otherPseudonym || `Guest ${otherId?.slice(-6)}`,
      subtitle: "A private booth. Only the two of you.",
      icon: Lock,
    };
  }, [activeRoom, acceptedPrivateRooms, meId]);

  const ActiveIcon = activeHeader.icon;

  return (
    <div
      className="min-h-screen text-slate-900 flex flex-col bg-cover bg-center bg-no-repeat bg-fixed"
      style={{
        backgroundImage:
          "url('https://customer-assets-v7afamib.emergentagent.net/wingman/4b71e223-37e4-4615-8453-e68a2ac08d9e/attachments/2c3b26e97c504cbd8f2491cc98f6c5ae_ChatGPT%20Image%20Sep%204%2C%202026%2C%2004_45_17%20PM.png')",
      }}
      data-testid="mahfil-root"
    >
      <header className="border-b border-white/50 bg-white/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="mahfil-brand-home-link"
          >
            <Flame className="w-5 h-5 text-amber-600 candle-flicker" />
            <span className="font-serif-editorial text-2xl tracking-wide text-slate-900 group-hover:text-amber-700 transition-colors">
              Kuch Kahna Hai
            </span>
            <span className="hidden sm:inline text-slate-500 mx-2">·</span>
            <span className="hidden sm:inline font-mono-mini text-[20px] uppercase tracking-[0.3em] text-slate-600">
              Mahfil
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              data-testid="mahfil-back-dashboard-btn"
              onClick={() => navigate("/app/dashboard")}
              className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-700 hover:text-amber-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Your room
            </button>
            <div
              className="flex items-center gap-2 rounded-full bg-white/80 border border-slate-300 px-3 py-1.5"
              data-testid="mahfil-me-badge"
            >
              <span className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 grid place-items-center text-white text-[10px] font-bold">
                {(user?.pseudonym || "?").slice(0, 1).toUpperCase()}
              </span>
              <span className="text-sm text-slate-900">{user?.pseudonym}</span>
            </div>
            <button
              data-testid="mahfil-logout-btn"
              onClick={doLogout}
              className="p-2 rounded-full text-slate-500 hover:text-rose-600 hover:bg-rose-100 transition-all"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto grid lg:grid-cols-[280px_1fr] gap-0 lg:gap-6 px-4 sm:px-6 py-6">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col gap-6 lg:sticky lg:top-24 self-start max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
          <button
            data-testid="mahfil-room-common-btn"
            onClick={() => setActiveRoom(COMMON_ROOM)}
            className={`text-left rounded-2xl p-4 border transition-all ${
              activeRoom === COMMON_ROOM
                ? "border-amber-500/70 bg-amber-100/70"
                : "border-white/60 bg-white/70 hover:border-amber-500/50 hover:bg-white/85"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Hash className="w-4 h-4 text-amber-600" />
              <span className="font-serif-editorial text-lg text-slate-900">The Mahfil</span>
            </div>
            <div className="text-xs text-slate-600">A common room · everyone</div>
          </button>

          {/* Incoming requests */}
          {pendingIncoming.length > 0 && (
            <section data-testid="mahfil-incoming-requests">
              <div className="font-mono-mini text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-3">
                Whispers for you
              </div>
              <ul className="flex flex-col gap-2">
                {pendingIncoming.map((r) => (
                  <li
                    key={r.id}
                    data-testid="mahfil-request-item"
                    className="rounded-2xl border border-amber-300/70 bg-white/85 p-3"
                  >
                    <div className="text-sm text-slate-900">
                      <span className="font-medium text-amber-700">{r.from_pseudonym}</span>{" "}
                      wants to talk in private.
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        data-testid="mahfil-request-accept-btn"
                        onClick={() => accept(r)}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-400 text-white text-xs font-semibold px-3 py-1.5 transition-all"
                      >
                        <Check className="w-3.5 h-3.5" /> Accept
                      </button>
                      <button
                        data-testid="mahfil-request-decline-btn"
                        onClick={() => decline(r)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-300 hover:border-rose-500/60 text-slate-700 hover:text-rose-600 text-xs px-3 py-1.5 transition-all"
                      >
                        <X className="w-3.5 h-3.5" /> Not now
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Private rooms */}
          {acceptedPrivateRooms.length > 0 && (
            <section>
              <div className="font-mono-mini text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-3">
                Private booths
              </div>
              <ul className="flex flex-col gap-2">
                {acceptedPrivateRooms.map((r) => (
                  <li key={r.room}>
                    <button
                      data-testid="mahfil-private-room-btn"
                      onClick={() => setActiveRoom(r.room)}
                      className={`w-full text-left rounded-2xl p-3 border transition-all ${
                        activeRoom === r.room
                          ? "border-amber-500/70 bg-amber-100/70"
                          : "border-white/60 bg-white/70 hover:border-amber-500/50"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Lock className="w-3.5 h-3.5 text-amber-600" />
                        <span className="text-sm text-slate-900">{r.otherPseudonym}</span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Online users */}
          <section data-testid="mahfil-users-panel">
            <div className="font-mono-mini text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-3 flex items-center gap-2">
              <UsersIcon className="w-3 h-3" /> In the room now
            </div>
            <ul className="flex flex-col gap-2">
              {users.length === 0 && (
                <li className="text-xs text-slate-500 italic">Just you and the candles.</li>
              )}
              {users.map((u) => {
                if (u.is_me) return null;
                const existing = requests.find(
                  (r) =>
                    (r.from_user_id === u.id || r.to_user_id === u.id) &&
                    (r.status === "pending" || r.status === "accepted"),
                );
                const isAccepted = existing?.status === "accepted";
                const isPending = existing?.status === "pending";
                let action;
                if (isAccepted) {
                  action = (
                    <button
                      onClick={() => setActiveRoom(existing.room)}
                      className="text-[10px] font-mono-mini uppercase tracking-widest text-amber-700 hover:text-amber-600"
                    >
                      Open
                    </button>
                  );
                } else if (isPending) {
                  action = (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-mono-mini uppercase tracking-widest text-slate-500"
                      data-testid="mahfil-pending-badge"
                    >
                      <Clock className="w-3 h-3" /> Pending
                    </span>
                  );
                } else {
                  action = (
                    <button
                      data-testid="mahfil-ask-private-btn"
                      onClick={() => askPrivate(u)}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-300 hover:border-amber-500/70 text-slate-700 hover:text-amber-700 text-[10px] font-mono-mini uppercase tracking-widest px-2.5 py-1 transition-all"
                    >
                      <Lock className="w-3 h-3" /> Ask
                    </button>
                  );
                }
                return (
                  <li
                    key={u.id}
                    data-testid="mahfil-user-item"
                    className="flex items-center justify-between gap-2 rounded-xl bg-white/75 border border-white/60 px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-sm text-slate-900 truncate">{u.pseudonym}</span>
                    </div>
                    {action}
                  </li>
                );
              })}
            </ul>
            {pendingOutgoing.length > 0 && (
              <div className="mt-4 text-[10px] font-mono-mini uppercase tracking-widest text-slate-500">
                {pendingOutgoing.length} waiting reply
              </div>
            )}
          </section>
        </aside>

        {/* Chat area */}
        <section className="flex flex-col rounded-3xl border border-white/30 bg-transparent min-h-[70vh]">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/40 bg-white/40 backdrop-blur-md rounded-t-3xl">
            <div className="flex items-center gap-3 min-w-0">
              <ActiveIcon className="w-4 h-4 text-amber-600 shrink-0" />
              <div className="min-w-0">
                <div
                  className="font-serif-editorial text-xl truncate text-slate-900"
                  data-testid="mahfil-active-room-title"
                >
                  {activeHeader.title}
                </div>
                <div className="text-2xl font-black text-black">{activeHeader.subtitle}</div>
              </div>
            </div>
            {activeRoom !== COMMON_ROOM && (
              <button
                onClick={() => setActiveRoom(COMMON_ROOM)}
                className="text-xs font-mono-mini uppercase tracking-widest text-slate-600 hover:text-amber-700"
                data-testid="mahfil-back-common-btn"
              >
                ← Back to Mahfil
              </button>
            )}
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-6 space-y-4"
            data-testid="mahfil-messages-list"
          >
            {messages.length === 0 && (
              <div className="text-center text-slate-500 mt-16">
                <MessageCircle className="w-6 h-6 mx-auto mb-3 text-amber-600" />
                <div className="font-serif-editorial text-xl text-slate-800">
                  It’s quiet here.
                </div>
                <div className="mt-1 text-sm">Be the first to say something.</div>
              </div>
            )}
            {messages.map((m) => {
              const mine = m.from_user_id === meId;
              return (
                <div
                  key={m.id}
                  data-testid="mahfil-message-item"
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-3 border shadow-sm ${
                      mine
                        ? "bg-amber-100 border-amber-300 text-slate-900"
                        : "bg-white border-slate-200 text-slate-900"
                    }`}
                  >
                    {!mine && (
                      <div className="text-[10px] font-mono-mini uppercase tracking-widest text-amber-700 mb-1">
                        {m.from_pseudonym}
                      </div>
                    )}
                    <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {m.body}
                    </div>
                    <div
                      className={`text-[10px] font-mono-mini mt-1 ${
                        mine ? "text-amber-700/70 text-right" : "text-slate-500"
                      }`}
                    >
                      {timeShort(m.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <form
            onSubmit={sendMessage}
            className="px-3 py-3 border-t border-white/40 bg-white/40 backdrop-blur-md rounded-b-3xl flex items-end gap-2"
            data-testid="mahfil-composer"
          >
            <textarea
              data-testid="mahfil-message-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              placeholder={
                activeRoom === COMMON_ROOM
                  ? "Say something to the room…"
                  : "Whisper in private…"
              }
              rows={1}
              className="flex-1 resize-none bg-white/95 border border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none rounded-2xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 max-h-32"
            />
            <button
              type="submit"
              data-testid="mahfil-send-btn"
              disabled={sending || !body.trim()}
              className="inline-flex items-center justify-center rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-white w-11 h-11 transition-all shadow-[0_0_24px_-8px_rgba(245,158,11,0.6)]"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
